/**
 * ResortLandingPage — multi-venue resort grouping landing.
 *
 * Route: /resort/:resortSlug
 *
 * Backed by `GET /api/directory/resorts/{slug}` which returns the
 * parent resort metadata + an array of sister-venue summaries
 * (tagged via `parent_resort_slug` in `demo_restaurants`).
 *
 * Design language is intentionally distinctive — warm, dark
 * cinematic hero with shifting venue collage + gold accents,
 * tier-colored badges on each venue card, asymmetric layout.
 * Avoids the generic light-card grid common to AI-generated UIs.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronRight, MapPin, Globe2, Phone,
              Sparkles, ArrowUpRight } from "lucide-react";

const TIER_BADGE = {
  platinum: { label: "Platinum",  cls: "bg-[#E5C76B] text-[#1a1208] border-[#C9A951]" },
  gold:     { label: "Gold",      cls: "bg-[#F1D879] text-[#1a1208] border-[#D4B860]" },
  silver:   { label: "Silver",    cls: "bg-[#D6D6D6] text-[#1a1208] border-[#B5B5B5]" },
  free:     { label: "Listed",    cls: "bg-stone-200 text-stone-800 border-stone-400" },
};


export default function ResortLandingPage() {
  const { resortSlug } = useParams();
  const [data, setData] = useState(null);
  const [err,  setErr]  = useState("");

  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    fetch(`${base}/api/directory/resorts/${resortSlug}`)
      .then((r) => r.ok ? r.json() : Promise.reject(r.status))
      .then(setData)
      .catch((e) => setErr(`Could not load resort (${e})`));
  }, [resortSlug]);

  if (err)   return <p className="p-8 text-rose-600">{err}</p>;
  if (!data) return <ResortSkeleton />;

  const { name, address, suburb, postcode, state,
                website, venues = [] } = data;
  // Cycle hero images from the first 3 venues to build a collage.
  const heroBg = venues.slice(0, 3)
    .map((v) => v.hero_image_url)
    .filter(Boolean);

  return (
    <div className="min-h-screen bg-[#0B0907] text-stone-100"
            data-testid="resort-landing">

      {/* ─── Hero ─────────────────────────────────────────────── */}
      <section className="relative h-[78vh] min-h-[520px] overflow-hidden">
        <div className="absolute inset-0 grid grid-cols-3">
          {(heroBg.length ? heroBg : [null,null,null])
            .map((url, i) => (
              <div key={i} style={{
                backgroundImage:    url ? `url(${url})` : undefined,
                backgroundSize:     "cover",
                backgroundPosition: "center",
                opacity:            url ? 1 : 0.25,
              }} className="bg-stone-900 transition-opacity duration-700" />
            ))}
        </div>
        <div className="absolute inset-0 bg-gradient-to-b
                                from-black/70 via-black/55 to-[#0B0907]" />
        <div className="absolute inset-0 flex flex-col
                                justify-end pb-20 px-6 md:px-16">
          <div className="max-w-4xl">
            <p className="text-[#E5C76B] tracking-[0.4em] uppercase text-xs mb-5"
                  data-testid="resort-eyebrow">
              <Sparkles className="inline w-3 h-3 mr-2 -mt-0.5" />
              A Cabravale dining precinct
            </p>
            <h1 className="font-serif text-5xl md:text-7xl
                                leading-[0.95] mb-6 text-stone-50"
                  data-testid="resort-name">
              {name}
            </h1>
            <p className="text-stone-300 text-lg md:text-xl max-w-2xl">
              Six dining venues. One unforgettable address.
              From open-flame Wagyu and live-tank lobster yum cha to
              a 2,000sqm Southeast Asian food precinct and Western
              Sydney&apos;s first premium poolside lounge.
            </p>
            <div className="mt-8 flex flex-wrap gap-4 text-sm text-stone-300">
              <span className="inline-flex items-center gap-2"
                       data-testid="resort-address">
                <MapPin className="w-4 h-4 text-[#E5C76B]" />
                {address}, {suburb} {state} {postcode}
              </span>
              {website && (
                <a href={website} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-2
                                    hover:text-[#E5C76B] transition-colors"
                      data-testid="resort-website">
                  <Globe2 className="w-4 h-4" />
                  cabravale.com.au
                  <ArrowUpRight className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ─── Venue grid ───────────────────────────────────────── */}
      <section className="px-6 md:px-16 py-20 max-w-7xl mx-auto">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <h2 className="font-serif text-3xl md:text-5xl text-stone-50">
            The Venues
          </h2>
          <p className="text-stone-400 text-sm max-w-md">
            Each restaurant tells its own story — tap through to see
            menus, hours, allergen-aware AI host and reservations.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
          {venues.map((v, idx) => (
            <VenueCard key={v.slug} venue={v}
                          featured={v.tier === "platinum"}
                          flip={idx % 2 === 1} />
          ))}
        </div>
      </section>

      <footer className="px-6 md:px-16 py-12 border-t border-stone-800
                                text-center text-stone-500 text-xs tracking-widest uppercase">
        Powered by HustleEats · {venues.length} venues active
      </footer>
    </div>
  );
}


function VenueCard({ venue, featured, flip }) {
  const t = TIER_BADGE[venue.tier] || TIER_BADGE.free;
  return (
    <Link to={`/r/${venue.slug}`}
            data-testid={`venue-card-${venue.slug}`}
            className={`group relative block overflow-hidden
                          rounded-2xl border border-stone-800 bg-stone-900
                          hover:border-[#E5C76B]/50 transition-all
                          duration-500 ${featured ? "md:col-span-1" : ""}`}>
      <div className="aspect-[5/4] w-full overflow-hidden">
        {venue.hero_image_url ? (
          <img src={venue.hero_image_url}
                  alt={venue.name}
                  loading="lazy"
                  className="w-full h-full object-cover
                                group-hover:scale-105
                                transition-transform duration-700" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br
                                  from-stone-800 to-stone-950" />
        )}
      </div>

      {/* Tier badge */}
      <span className={`absolute top-4 ${flip ? "left-4" : "right-4"}
                              text-[10px] font-medium tracking-[0.2em]
                              uppercase px-2.5 py-1 rounded-full
                              border ${t.cls}`}
              data-testid={`venue-tier-${venue.slug}`}>
        {t.label}
      </span>

      <div className="p-6 md:p-7">
        <p className="text-[10px] text-[#E5C76B] tracking-[0.3em]
                            uppercase mb-2 font-medium">
          {venue.cuisine}
        </p>
        <h3 className="font-serif text-2xl md:text-3xl text-stone-50 mb-2
                             group-hover:text-[#F2D879] transition-colors">
          {venue.name}
        </h3>
        {venue.tagline && (
          <p className="text-stone-400 text-sm leading-relaxed mb-4
                              line-clamp-2">
            {venue.tagline}
          </p>
        )}
        <div className="flex items-center justify-between mt-5">
          {venue.chef && (
            <span className="text-xs text-stone-500">
              Chef · <span className="text-stone-300">{venue.chef}</span>
            </span>
          )}
          <span className="ml-auto inline-flex items-center gap-1
                                  text-xs text-stone-400
                                  group-hover:text-[#E5C76B]
                                  group-hover:translate-x-1
                                  transition-all duration-300">
            Visit venue
            <ChevronRight className="w-3.5 h-3.5" />
          </span>
        </div>
      </div>
    </Link>
  );
}


function ResortSkeleton() {
  return (
    <div className="min-h-screen bg-[#0B0907]">
      <div className="h-[78vh] bg-stone-900 animate-pulse" />
      <div className="px-6 md:px-16 py-20 max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-8">
          {[1,2,3,4].map((i) => (
            <div key={i} className="h-96 rounded-2xl bg-stone-900
                                                  animate-pulse" />
          ))}
        </div>
      </div>
    </div>
  );
}
